import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Role } from '@/types';
import { useAuth } from '@/features/auth/AuthContext';
import {
  useCreateUser,
  useDeleteUser,
  useUpdateUserRole,
  useUsers,
} from '@/features/users/hooks';
import { ApiError } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { inputClass } from '@/components/ui/Field';

const roleOptions: Role[] = ['Administrador', 'Engenheiro', 'Encarregado', 'Consulta'];

export function UsersManager() {
  const { t } = useTranslation();
  const { user: current } = useAuth();

  const usersQuery = useUsers();
  const createUser = useCreateUser();
  const updateRole = useUpdateUserRole();
  const deleteUser = useDeleteUser();

  const [nome, setNome] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('Encarregado');
  const [error, setError] = useState<string | null>(null);

  async function handleAdd() {
    setError(null);
    if (!nome.trim() || !username.trim() || password.length < 6) {
      setError(t('profile.fillUserFields'));
      return;
    }
    try {
      await createUser.mutateAsync({
        displayName: nome.trim(),
        username: username.trim().toLowerCase(),
        password,
        role,
      });
      setNome('');
      setUsername('');
      setPassword('');
      setRole('Encarregado');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  }

  return (
    <Card className="flex flex-col gap-2.5 p-4">
      <h2 className="text-base font-bold">{t('profile.users')}</h2>

      {usersQuery.data?.map((u) => {
        const isSelf = u.id === current?.id;
        return (
          <div
            key={u.id}
            className="flex items-center justify-between gap-2 border-b border-[#e9ecf7] py-2 text-sm"
          >
            <span>
              {u.displayName} <span className="text-xs text-conduril-muted">({u.username})</span>
            </span>
            <div className="flex items-center gap-2">
              <select
                value={u.role}
                disabled={isSelf || updateRole.isPending}
                onChange={(e) => updateRole.mutate({ id: u.id, role: e.target.value as Role })}
                className="rounded-full bg-conduril-tint px-2.5 py-1 text-xs font-bold text-conduril disabled:opacity-70"
              >
                {roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {t(`roles.${r}`)}
                  </option>
                ))}
              </select>
              {!isSelf && (
                <button
                  type="button"
                  aria-label={t('common.remove')}
                  onClick={() =>
                    window.confirm(t('profile.confirmRemoveUser', { name: u.displayName })) &&
                    deleteUser.mutate(u.id)
                  }
                  className="h-8 w-8 rounded-lg bg-danger-bg text-danger-text"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        );
      })}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder={t('profile.name')}
          className={inputClass}
        />
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t('profile.username')}
          autoCapitalize="none"
          className={inputClass}
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('profile.password')}
          className={inputClass}
        />
        <select value={role} onChange={(e) => setRole(e.target.value as Role)} className={inputClass}>
          {roleOptions.map((r) => (
            <option key={r} value={r}>
              {t(`roles.${r}`)}
            </option>
          ))}
        </select>
      </div>

      <ErrorBanner message={error} />

      <button
        type="button"
        onClick={handleAdd}
        disabled={createUser.isPending}
        className="min-h-[44px] rounded-lg bg-conduril py-2.5 text-sm font-bold text-white hover:bg-conduril-dark disabled:opacity-60"
      >
        ＋ {t('profile.addUser')}
      </button>

      <span className="text-xs text-conduril-muted">{t('roles.help')}</span>
    </Card>
  );
}
